import axios from 'axios';
import { CompleteTutorSimpleProfileRequest } from './models/Auth';
import { GenerateUploadUrlsRequest } from './models/GenerateUploadUrlsRequest';
import { GenerateUploadUrlsResponse } from './models/GenerateUploadUrlsResponse';
import { HostOrganizationUserResponse } from './models/HostOrganizationUser';
import { Room } from './models/Room';
import {
  CancelTutorBookingChangeRequest,
  ProposeTutorBookingChangeRequest,
} from './models/TutorBookingChange';
import {
  UpdateUserTutorProfileRequest,
  TutorBookingFilter,
  TutorBookingNeedsTutorActionCountResponse,
  TutorBookingSummaryCountsResponse,
  TutorBookingsPageResponse,
  TutorDailyAvailability,
  TutorInviteRequest,
  TutorInviteResponse,
  UserTutorBookingDetails,
  UserTutorProfileSettings,
  TutorSearchResult,
  HostTutorResponse,
  TutorCity,
} from './models/Tutor';
import { StripeConnectExpressAccount } from './models/Organization';
import { TutorReviewWithUsername } from './models/TutorReview';
import {
  mapHostTutorResponseFromApi,
  mapTutorProfileSettingsFromApi,
  mapTutorSearchResultFromApi,
} from './utils/tutorSessionType';
import { applyClientChannelHeader } from './utils/clientChannel';
import { applyAllowTestOrgsHeader } from './utils/experimentalFeatures';
import { PassProviderDetail, PassProviderEvent } from './models/Pass';

const publicBaseUrl = process.env.REACT_APP_API_BASE_URL + '/public';
const publicAuthBaseUrl = process.env.REACT_APP_API_BASE_URL + '/public/auth';
const hostBaseUrl = process.env.REACT_APP_API_BASE_URL + '/host';
const tutorBaseUrl = process.env.REACT_APP_API_BASE_URL + '/tutor';
const userBaseUrl = process.env.REACT_APP_API_BASE_URL + '/user';

export class TutorApiClient {
  private publicAxios = axios.create({
    baseURL: publicBaseUrl,
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
  });

  private hostAxios = axios.create({
    baseURL: hostBaseUrl,
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
  });

  private tutorAxios = axios.create({
    baseURL: tutorBaseUrl,
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
  });

  private userAxios = axios.create({
    baseURL: userBaseUrl,
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
  });

  /** Individual-tutor Stripe calls use host endpoints and must not send X-OrganizationId. */
  private tutorStripeAxios = axios.create({
    baseURL: hostBaseUrl,
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
  });

  private publicAuthAxios = axios.create({
    baseURL: publicAuthBaseUrl,
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
  });

  constructor() {
    const addAuthHeaders = (config: any) => {
      const accessToken = localStorage.getItem('access_token');
      const assumedOrganizationId = localStorage.getItem('assumed_organization_id');
      const assumedUserId = localStorage.getItem('assumed_user_id');
      if (accessToken) {
        config.headers.Authorization = `Bearer ${accessToken}`;
      }
      if (assumedOrganizationId) {
        config.headers['X-OrganizationId'] = assumedOrganizationId;
      }
      if (assumedUserId) {
        config.headers['X-UserId'] = assumedUserId;
      }
      applyClientChannelHeader(config.headers);
      applyAllowTestOrgsHeader(config.headers);
      return config;
    };

    const addAuthHeadersWithoutOrganization = (config: any) => {
      const accessToken = localStorage.getItem('access_token');
      const assumedUserId = localStorage.getItem('assumed_user_id');
      if (accessToken) {
        config.headers.Authorization = `Bearer ${accessToken}`;
      }
      if (assumedUserId) {
        config.headers['X-UserId'] = assumedUserId;
      }
      applyClientChannelHeader(config.headers);
      applyAllowTestOrgsHeader(config.headers);
      return config;
    };

    this.publicAxios.interceptors.request.use((config) => {
      applyClientChannelHeader(config.headers);
      applyAllowTestOrgsHeader(config.headers);
      return config;
    });
    this.publicAuthAxios.interceptors.request.use((config) => {
      applyClientChannelHeader(config.headers);
      return config;
    });
    this.hostAxios.interceptors.request.use(addAuthHeaders);
    this.tutorAxios.interceptors.request.use(addAuthHeaders);
    this.userAxios.interceptors.request.use(addAuthHeaders);
    this.tutorStripeAxios.interceptors.request.use(addAuthHeadersWithoutOrganization);
  }

  /** GET /v1/public/tutors/search/online — online tutor discovery (no city). */
  public async searchOnlineTutors(): Promise<TutorSearchResult[]> {
    const response = await this.publicAxios.get<
      Array<TutorSearchResult & { heading?: string | null; tutorHeadline?: string | null }>
    >('/tutors/search/online');
    return response.data.map((tutor) => mapTutorSearchResultFromApi(tutor));
  }

  /**
   * GET /v1/public/tutors/cities — distinct cities for offline tutor discovery.
   * `sessionType` defaults to `AT_TEACHER` on the backend when omitted.
   * `AT_TEACHER` cities come from attached spaces; `AT_STUDENT` from `studentLessonCities`.
   */
  public async listTutorCities(
    sessionType: 'AT_TEACHER' | 'AT_STUDENT' = 'AT_TEACHER',
  ): Promise<TutorCity[]> {
    const response = await this.publicAxios.get<string[]>('/tutors/cities', {
      params: { sessionType },
    });
    return response.data;
  }

  /**
   * GET /v1/public/tutors/search/offline?city=... — offline tutor discovery.
   * `city` is required by the backend contract.
   * `sessionType` defaults to `AT_TEACHER` on the backend when omitted.
   */
  public async searchOfflineTutors(
    city: string,
    sessionType: 'AT_TEACHER' | 'AT_STUDENT' = 'AT_TEACHER',
  ): Promise<TutorSearchResult[]> {
    const response = await this.publicAxios.get<
      Array<TutorSearchResult & { heading?: string | null; tutorHeadline?: string | null }>
    >('/tutors/search/offline', {
      params: { city, sessionType },
    });
    return response.data.map((tutor) => mapTutorSearchResultFromApi(tutor));
  }

  /** GET /v1/public/spaces/{spaceId}/tutors — public tutor roster for one space. */
  public async getPublicTutorsBySpaceId(spaceId: string): Promise<TutorSearchResult[]> {
    const response = await this.publicAxios.get<
      Array<TutorSearchResult & { heading?: string | null; tutorHeadline?: string | null }>
    >(`/spaces/${spaceId}/tutors`);
    return response.data.map((tutor) => mapTutorSearchResultFromApi(tutor));
  }

  /** GET /v1/public/organizations/{organizationId}/tutors — public tutor roster for one organization. */
  public async getPublicTutorsByOrganizationId(organizationId: string): Promise<TutorSearchResult[]> {
    const response = await this.publicAxios.get<
      Array<TutorSearchResult & { heading?: string | null; tutorHeadline?: string | null }>
    >(`/organizations/${organizationId}/tutors`);
    return response.data.map((tutor) => mapTutorSearchResultFromApi(tutor));
  }

  /** GET /v1/public/tutors/{tutorId} — single public tutor discovery record. */
  public async getPublicTutorById(tutorId: string): Promise<TutorSearchResult> {
    const response = await this.publicAxios.get<
      TutorSearchResult & { heading?: string | null; tutorHeadline?: string | null }
    >(`/tutors/${tutorId}`);
    return mapTutorSearchResultFromApi(response.data);
  }

  /** GET /v1/public/tutors/{tutorId}/reviews — public tutor reviews with reviewer username. */
  public async getTutorReviews(tutorId: string): Promise<TutorReviewWithUsername[]> {
    const response = await this.publicAxios.get<TutorReviewWithUsername[]>(
      `/tutors/${tutorId}/reviews`,
    );
    return response.data;
  }

  public async getTutorDailyAvailability(tutorIds: string[], day: string): Promise<TutorDailyAvailability[]> {
    const response = await this.publicAxios.get<TutorDailyAvailability[]>('/availability/tutors/daily', {
      params: { tutorIds, day },
      paramsSerializer: {
        indexes: null,
      },
    });
    return response.data;
  }

  /** GET /v1/host/tutors — organization tutor roster for host workspace. */
  public async getMyTutors(): Promise<HostTutorResponse[]> {
    const response = await this.hostAxios.get<HostTutorResponse[]>('/tutors');
    return response.data.map((tutor) => mapHostTutorResponseFromApi(tutor));
  }

  /** GET /v1/tutor/booking-clients — tutor lesson clients only (manual booking picker). */
  public async getTutorBookingClients(): Promise<HostOrganizationUserResponse[]> {
    const response = await this.tutorAxios.get<HostOrganizationUserResponse[]>('/booking-clients');
    return response.data;
  }

  /** GET /v1/host/rooms?spaceId=... — rooms for offline lesson assignment. */
  public async getRooms(spaceId: string): Promise<Room[]> {
    const response = await this.hostAxios.get<Room[]>('/rooms', {
      params: { spaceId },
    });
    return response.data;
  }

  public async createTutorInvite(request: TutorInviteRequest): Promise<TutorInviteResponse> {
    const response = await this.hostAxios.post<TutorInviteResponse>('/tutors/invitations', request);
    return response.data;
  }

  /** POST /v1/host/tutors/self — attach the host tutor profile to an organization space. */
  public async addHostAsTutor(spaceId: string): Promise<void> {
    await this.hostAxios.post('/tutors/self', { spaceId });
  }

  public async getTutorBookingRequests(
    page: number = 0,
    size: number = 10,
    filter: TutorBookingFilter = 'all',
  ): Promise<TutorBookingsPageResponse> {
    const response = await this.tutorAxios.get<TutorBookingsPageResponse>('/tutor-bookings', {
      params: { page, size, filter },
    });
    return response.data;
  }

  /** GET /v1/tutor/tutor-bookings/{bookingId} */
  public async getTutorBookingDetails(bookingId: string): Promise<UserTutorBookingDetails> {
    const response = await this.tutorAxios.get<UserTutorBookingDetails>(`/tutor-bookings/${bookingId}`);
    return response.data;
  }

  /** GET /v1/tutor/tutor-bookings/summary — tab badge counts. */
  public async getTutorBookingsSummary(): Promise<TutorBookingSummaryCountsResponse> {
    const response = await this.tutorAxios.get<TutorBookingSummaryCountsResponse>('/tutor-bookings/summary');
    return response.data;
  }

  /** GET /v1/tutor/tutor-bookings/needs-tutor-action/count — actionable badge total only. */
  public async getTutorBookingsNeedsTutorActionCount(): Promise<TutorBookingNeedsTutorActionCountResponse> {
    const response = await this.tutorAxios.get<TutorBookingNeedsTutorActionCountResponse>(
      '/tutor-bookings/needs-tutor-action/count',
    );
    return response.data;
  }

  public async acceptTutorBooking(bookingId: string): Promise<void> {
    await this.tutorAxios.post(`/tutor-bookings/${bookingId}/accept`);
  }

  public async declineTutorBooking(bookingId: string, reason: string): Promise<void> {
    await this.tutorAxios.post(`/tutor-bookings/${bookingId}/decline`, { reason });
  }

  public async acceptTutorBookingSeries(recurringSeriesId: string): Promise<void> {
    await this.tutorAxios.post(`/tutor-bookings/series/${recurringSeriesId}/accept`);
  }

  public async acceptSelectedTutorBookingSeries(
    recurringSeriesId: string,
    bookingIds: string[],
    declineReason: string,
  ): Promise<void> {
    await this.tutorAxios.post(`/tutor-bookings/series/${recurringSeriesId}/accept-selected`, {
      bookingIds,
      declineReason,
    });
  }

  public async declineTutorBookingSeries(recurringSeriesId: string, reason: string): Promise<void> {
    await this.tutorAxios.post(`/tutor-bookings/series/${recurringSeriesId}/decline`, { reason });
  }

  /** POST /v1/tutor/tutor-bookings/{bookingId}/changes */
  public async proposeTutorBookingChange(
    bookingId: string,
    request: ProposeTutorBookingChangeRequest,
  ): Promise<void> {
    await this.tutorAxios.post(`/tutor-bookings/${bookingId}/changes`, request);
  }

  /** POST /v1/tutor/tutor-bookings/{bookingId}/changes/{changeId}/cancel */
  public async cancelTutorBookingChange(
    bookingId: string,
    changeId: string,
    request: CancelTutorBookingChangeRequest,
  ): Promise<void> {
    await this.tutorAxios.post(`/tutor-bookings/${bookingId}/changes/${changeId}/cancel`, request);
  }

  /** POST /v1/tutor/tutor-bookings/{bookingId}/refund */
  public async refundTutorBooking(bookingId: string, request: { refund_reason?: string } = {}): Promise<void> {
    await this.tutorAxios.post(`/tutor-bookings/${bookingId}/refund`, request);
  }

  /** POST /v1/tutor/tutor-bookings/{bookingId}/collect-payment — mark on-site payment collected. */
  public async collectTutorBookingPayment(bookingId: string): Promise<void> {
    await this.tutorAxios.post(`/tutor-bookings/${bookingId}/collect-payment`);
  }

  /** POST /v1/public/auth/tutor/profile/complete — only accessToken + name; listing defaults are created server-side. */
  public async completeTutorProfile(request: CompleteTutorSimpleProfileRequest): Promise<void> {
    await this.publicAuthAxios.post('/tutor/profile/complete', request);
  }

  /** GET /v1/tutor/listing */
  public async getTutorProfileSettings(): Promise<UserTutorProfileSettings> {
    const response = await this.tutorAxios.get<UserTutorProfileSettings>('/listing');
    return mapTutorProfileSettingsFromApi(response.data);
  }

  /** PUT /v1/tutor/listing — listing replace (headline, overview, skills, languages, profileImageUrl). Lesson options are host-catalog only. */
  public async updateTutorProfileSettings(request: UpdateUserTutorProfileRequest): Promise<void> {
    await this.tutorAxios.put<void>('/listing', request);
  }

  /** POST /v1/tutor/calendar-events — manual block for the authenticated tutor (calendar_event only). */
  public async createTutorCalendarEvent(request: {
    title: string;
    startTime: string;
    endTime: string;
  }): Promise<{ id: string }> {
    try {
      const response = await this.tutorAxios.post<{ id: string }>('/calendar-events', request);
      return response.data;
    } catch (error) {
      console.error('Error creating tutor calendar event:', error);
      throw error;
    }
  }

  /** DELETE /v1/tutor/calendar-events/{calendarEventId} — manual tutor block only. */
  public async deleteTutorCalendarEvent(calendarEventId: string): Promise<void> {
    try {
      await this.tutorAxios.delete(`/calendar-events/${calendarEventId}`);
    } catch (error) {
      console.error(`Error deleting tutor calendar event ${calendarEventId}:`, error);
      throw error;
    }
  }

  /** PUT /v1/host/tutors/{tutorId}/listing — host edit of an org tutor’s public listing. */
  public async updateHostTutorListing(
    tutorId: string,
    request: UpdateUserTutorProfileRequest,
  ): Promise<void> {
    await this.hostAxios.put<void>(`/tutors/${tutorId}/listing`, request);
  }

  /** PUT /v1/host/tutors/{tutorId}/visibility — toggle public discoverability for a roster tutor. */
  public async updateHostTutorVisibility(tutorId: string, visible: boolean): Promise<void> {
    await this.hostAxios.put<void>(`/tutors/${tutorId}/visibility`, { visible });
  }

  /** PUT /v1/host/tutors/{tutorId}/space — assign or reassign a roster tutor to an org space. */
  public async updateHostTutorSpace(tutorId: string, spaceId: string): Promise<void> {
    await this.hostAxios.put<void>(`/tutors/${tutorId}/space`, { spaceId });
  }

  /** POST /v1/tutor/assets/upload-urls */
  public async generateUploadUrls(request: GenerateUploadUrlsRequest): Promise<GenerateUploadUrlsResponse> {
    const response = await this.tutorAxios.post<GenerateUploadUrlsResponse>('/assets/upload-urls', request);
    return response.data;
  }

  /** POST /v1/host/stripe/connect-express-account (no X-OrganizationId) */
  public async createStripeConnectExpressAccount(): Promise<StripeConnectExpressAccount> {
    const response = await this.tutorStripeAxios.post<StripeConnectExpressAccount>('/stripe/connect-express-account');
    return response.data;
  }

  /** GET /v1/host/stripe/connect-express-account (no X-OrganizationId) */
  public async getStripeConnectExpressAccount(): Promise<StripeConnectExpressAccount> {
    const response = await this.tutorStripeAxios.get<StripeConnectExpressAccount>('/stripe/connect-express-account');
    return response.data;
  }

  /** POST /v1/host/stripe/connect-express-account/resync (no X-OrganizationId) */
  public async resyncStripeConnectExpressAccount(): Promise<StripeConnectExpressAccount> {
    const response = await this.tutorStripeAxios.post<StripeConnectExpressAccount>(
      '/stripe/connect-express-account/resync',
    );
    return response.data;
  }

  /** DELETE /v1/host/stripe/connect-express-account (no X-OrganizationId) */
  public async disconnectStripeConnectExpressAccount(): Promise<void> {
    await this.tutorStripeAxios.delete<void>('/stripe/connect-express-account');
  }

  /** GET /v1/tutor/pass-instances/{passId} — booked pass instance (not owner wallet). */
  public async getPassInstance(passId: string): Promise<PassProviderDetail> {
    try {
      const response = await this.tutorAxios.get<PassProviderDetail>(`/pass-instances/${passId}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching tutor pass instance ${passId}:`, error);
      throw error;
    }
  }

  /** GET /v1/tutor/pass-instances/{passId}/events */
  public async getPassInstanceEvents(passId: string): Promise<PassProviderEvent[]> {
    try {
      const response = await this.tutorAxios.get<PassProviderEvent[]>(`/pass-instances/${passId}/events`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching tutor pass instance events ${passId}:`, error);
      throw error;
    }
  }
}

export const tutorApiClient = new TutorApiClient();
